import { useFragment } from '@apollo/client';
import {
  Scoreboard,
  ScoreboardParticipant,
  ScoreboardParticipants,
  ScoreboardScore,
  ScoreboardScores
} from '@ps/sports-ui-component-library';
import type { EnrichedParticipants, EventId } from '@ps/web-sports-api-types';
import React from 'react';

import { useLiveData } from '@swf/sports/providers/LiveDataProvider';
import type { EventTypeId } from '@swf/sports/types/eventType';
import { getEventScoreboardFragment } from '@swf/sports/utils/getEventScoreboardFragment';
import { getIsAmericanFootballLiveData } from '@swf/sports/utils/getLiveData';
import useIsEventTypeLiveDataEnabled from '@swf/sports/utils/useIsEventTypeLiveDataEnabled';

export interface Props {
  eventId: EventId;
  eventTypeId: EventTypeId;
  enrichedParticipant: EnrichedParticipants[number] | undefined;
}

const SingleParticipantScoreboard: React.FC<Props> = ({
  eventId,
  eventTypeId,
  enrichedParticipant
}) => {
  const { variables } = useLiveData();
  const { data } = useFragment(getEventScoreboardFragment(eventTypeId, eventId, variables));
  const isEventTypeLiveDataEnabled = useIsEventTypeLiveDataEnabled(eventTypeId);

  if (!enrichedParticipant) return null;
  const { participant, type } = enrichedParticipant;

  if (!isEventTypeLiveDataEnabled) {
    return participant;
  }

  if (getIsAmericanFootballLiveData(eventTypeId, data)) {
    const score = type ? data.score : null;

    return (
      <Scoreboard variant="lite">
        <ScoreboardParticipants>
          <ScoreboardParticipant name={participant} />
        </ScoreboardParticipants>
        <ScoreboardScores>
          <ScoreboardScore
            score={[(type === 'HOME' ? score?.home : score?.away) ?? null]}
            isDoubleDigit
          />
        </ScoreboardScores>
      </Scoreboard>
    );
  }

  return participant;
};

export default SingleParticipantScoreboard;
